import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Read a team's season year as a number, whatever type it's stored as.
 *
 * Older team docs (and any doc last written by the Settings → Team Info form)
 * store `seasonYear` as a STRING, while the rollover engine writes a NUMBER.
 * Every comparison against a game's `seasonYear` tag must go through here, or
 * "2026" !== 2026 silently filters out the entire season.
 */
export function seasonYearOf(
  team: { seasonYear?: number | string } | null | undefined,
): number | null {
  const n = Number(team?.seasonYear);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Filter games to a single season (the team's current season, or a past season
 * when browsing Season History). A game belongs to the season when its
 * `season`/`seasonYear` tags match.
 *
 * Legacy fallback: games written before season-tagging have no tags. They are
 * treated as belonging to the CURRENT season only (so nothing disappears before
 * the first rollover establishes a boundary). Pass `isCurrent: false` when
 * viewing a past season so untagged games never leak into history.
 *
 * Years are compared numerically on both sides — see seasonYearOf above.
 */
export function gamesForSeason<T extends { season?: string; seasonYear?: number | string }>(
  games: T[],
  season: string,
  year: number | string,
  isCurrent = true,
): T[] {
  const targetYear = Number(year);
  return games.filter((g) => {
    const untagged = g.season == null || g.seasonYear == null;
    if (untagged) return isCurrent;
    return g.season === season && Number(g.seasonYear) === targetYear;
  });
}

/** Convenience wrapper: games belonging to the team's current season. */
export function currentSeasonGames<T extends { season?: string; seasonYear?: number | string }>(
  games: T[],
  team: { season?: string; seasonYear?: number | string } | null | undefined,
): T[] {
  const year = seasonYearOf(team);
  if (!team?.season || year == null) return games;
  return gamesForSeason(games, team.season, year, true);
}

/** Recursively drop undefined values (Firestore rejects undefined). */
export function deepClean<T>(value: T): T {
  if (Array.isArray(value)) return value.map(deepClean) as any;
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(value)) if (v !== undefined) out[k] = deepClean(v);
    return out;
  }
  return value;
}

export const getDefensiveSignal = (player: any, position: string) => {
  if (!player || !player.attributes) return 'bg-zinc-500';

  const fielding = player.attributes.fielding || {};
  const pitching = player.attributes.pitching || {};
  const intangibles = player.attributes.intangibles || {};
  const primaryPos = player.primaryPosition || '';

  let score = 0;
  if (position === 'P') {
    score = ((pitching.velocity || 0) * 0.6 + (pitching.control || 0) * 0.4) * 0.7 +
            ((intangibles.iq || 0) * 0.6 + (intangibles.clutch || 0) * 0.4) * 0.3;
  } else if (primaryPos === 'C') {
    score = ((fielding.glove || 0) * 0.5 + (fielding.arm || 0) * 0.3 + (fielding.block || 0) * 0.2) * 0.7 +
            ((intangibles.iq || 0) * 0.6 + (intangibles.clutch || 0) * 0.4) * 0.3;
  } else {
    score = ((fielding.glove || 0) * 0.6 + (fielding.arm || 0) * 0.4) * 0.7 +
            ((intangibles.iq || 0) * 0.6 + (intangibles.clutch || 0) * 0.4) * 0.3;
  }
  
  if (score >= 3.5) return 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]';
  if (score >= 2) return 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]';
  if (score > 0) return 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]';
  return 'bg-zinc-500';
};

export const getDefensiveBorder = (player: any, position: string) => {
  if (!player || !player.attributes) return 'border-zinc-700';
  const signal = getDefensiveSignal(player, position);
  if (signal.includes('emerald')) return 'border-emerald-500/50';
  if (signal.includes('amber')) return 'border-amber-500/50';
  if (signal.includes('red')) return 'border-red-500/50';
  return 'border-zinc-700';
};

// Older player records stored some attributes under legacy names that were later
// renamed in the app (velo→velocity, ctrl→control, aggro→approach). The data was
// never migrated, so we normalize each player's attributes to the canonical names
// on load — purely in memory, no database writes. Old keys are dropped so the rest
// of the app (OVR, pitcher detection, the attribute form's labels) sees one
// consistent name set. New saves already write the canonical names.
export function normalizeAttributes(attributes: any): any {
  if (!attributes || typeof attributes !== 'object') return attributes;
  const a: any = { ...attributes };
  if (a.pitching && typeof a.pitching === 'object') {
    a.pitching = { ...a.pitching };
    if (a.pitching.velocity == null && a.pitching.velo != null) a.pitching.velocity = a.pitching.velo;
    if (a.pitching.control == null && a.pitching.ctrl != null) a.pitching.control = a.pitching.ctrl;
    delete a.pitching.velo;
    delete a.pitching.ctrl;
  }
  if (a.hitting && typeof a.hitting === 'object') {
    a.hitting = { ...a.hitting };
    if (a.hitting.approach == null && a.hitting.aggro != null) a.hitting.approach = a.hitting.aggro;
    delete a.hitting.aggro;
  }
  if (a.intangibles && typeof a.intangibles === 'object') {
    a.intangibles = { ...a.intangibles };
    if (a.intangibles.clutch == null && a.intangibles.comp != null) a.intangibles.clutch = a.intangibles.comp;
    delete a.intangibles.comp;
  }
  return a;
}

export function calculateOVR(attributes: any, primaryPosition?: string) {
  if (!attributes) return 0;

  const hitting = attributes.hitting || {};
  const fielding = attributes.fielding || {};
  const pitching = attributes.pitching || {};
  const intangibles = attributes.intangibles || {};

  const hittingScore = ((hitting.contact || 0) * 0.4 + (hitting.power || 0) * 0.3 + (hitting.eye || 0) * 0.2 + (hitting.approach || 0) * 0.1);
  // Block only counts for catchers; everyone else uses glove/arm only
  const fieldingScore = primaryPosition === 'C'
    ? ((fielding.glove || 0) * 0.5 + (fielding.arm || 0) * 0.3 + (fielding.block || 0) * 0.2)
    : ((fielding.glove || 0) * 0.6 + (fielding.arm || 0) * 0.4);
  const pitchingScore = ((pitching.velocity || 0) * 0.6 + (pitching.control || 0) * 0.4);
  const intangibleScore = ((intangibles.speed || 0) * 0.4 + (intangibles.iq || 0) * 0.4 + (intangibles.clutch || 0) * 0.2);

  // If pitching score is 0 (velocity=0 and control=0), player is not a pitcher — exclude that bucket
  const isPitcher = pitchingScore > 0;
  const sum = hittingScore + fieldingScore + (isPitcher ? pitchingScore : 0) + intangibleScore;
  const avg = sum / (isPitcher ? 4 : 3);
  return Math.round((avg / 5) * 99);
}

export function blendScoutingOVR(
  headCoachOVR: number,
  assistantRatings: Record<string, { ovr: number }> | undefined,
  include: boolean
): number {
  if (!include || !assistantRatings) return headCoachOVR;
  const assistantOVRs = Object.values(assistantRatings).map(r => r.ovr).filter(Boolean);
  if (assistantOVRs.length === 0) return headCoachOVR;
  const total = headCoachOVR + assistantOVRs.reduce((a, b) => a + b, 0);
  return Math.round(total / (1 + assistantOVRs.length));
}

export function suggestAttributesFromStats(stats: any) {
  const pa = stats?.pa || 0;
  const ip = stats?.ip || 0;

  // If no usable stats, return all defaults
  if (pa === 0 && ip === 0) {
    return {
      attributes: {
        hitting: { contact: 3, power: 3, eye: 3, approach: 3 },
        fielding: { glove: 3, arm: 3, block: 3 },
        intangibles: { speed: 3, iq: 3, clutch: 3 },
        pitching: { velocity: 0, control: 0 },
      },
      positions: { P: 0, C: 0, '1B': 0, '2B': 0, '3B': 0, SS: 0, LF: 0, CF: 0, RF: 0 },
      primaryPosition: '',
      secondaryPosition: '',
    };
  }

  let contact = 3, power = 3, eye = 3, approach = 3;
  let speed = 3;
  let velocity = 0, control = 0;

  // Hitting: contact from AVG
  if (pa > 0 && stats?.avg !== undefined) {
    const avg = stats.avg;
    if (avg < 0.100) contact = 1;
    else if (avg < 0.200) contact = 2;
    else if (avg < 0.300) contact = 3;
    else if (avg < 0.400) contact = 4;
    else contact = 5;
  }

  // Hitting: power from SLG
  if (pa > 0 && stats?.slg !== undefined) {
    const slg = stats.slg;
    if (slg < 0.200) power = 1;
    else if (slg < 0.350) power = 2;
    else if (slg < 0.500) power = 3;
    else if (slg < 0.700) power = 4;
    else power = 5;
  }

  // Hitting: eye from BB/PA ratio
  if (pa > 0 && stats?.bb !== undefined) {
    const bbRatio = stats.bb / pa;
    if (bbRatio < 0.05) eye = 1;
    else if (bbRatio < 0.10) eye = 2;
    else if (bbRatio < 0.17) eye = 3;
    else if (bbRatio < 0.25) eye = 4;
    else eye = 5;
  }

  // Intangibles: speed from SB count (only when PA >= 10)
  if (pa >= 10 && stats?.sb !== undefined) {
    const sb = stats.sb;
    if (sb === 0) speed = 2;
    else if (sb <= 2) speed = 3;
    else if (sb <= 5) speed = 4;
    else speed = 5;
  }

  // Pitching: velocity from K/IP ratio (only when IP >= 1)
  if (ip >= 1 && stats?.soAllowed !== undefined && stats?.ip > 0) {
    const kIp = stats.soAllowed / stats.ip;
    if (kIp < 0.5) velocity = 1;
    else if (kIp < 1.0) velocity = 2;
    else if (kIp < 1.5) velocity = 3;
    else if (kIp < 2.0) velocity = 4;
    else velocity = 5;
  }

  // Pitching: control from BB/INN (only when IP >= 1)
  if (ip >= 1 && stats?.bbInn !== undefined) {
    const bbInn = stats.bbInn;
    if (bbInn > 2.0) control = 1;
    else if (bbInn >= 1.5) control = 2;
    else if (bbInn >= 1.0) control = 3;
    else if (bbInn >= 0.5) control = 4;
    else control = 5;
  }

  return {
    attributes: {
      hitting: { contact, power, eye, approach },
      fielding: { glove: 3, arm: 3, block: 3 },
      intangibles: { speed, iq: 3, clutch: 3 },
      pitching: { velocity, control },
    },
    positions: { P: 0, C: 0, '1B': 0, '2B': 0, '3B': 0, SS: 0, LF: 0, CF: 0, RF: 0 },
    primaryPosition: '',
    secondaryPosition: '',
  };
}

// Map a player's GameChanger fieldTime (innings at each position) into Dugout's
// position model: primary/secondary + 0–5 rank per position. Top position is
// seeded at rank 4 (not 5) to leave room for the coach to push it up during
// scouting. Anything with at least 1 inning gets a non-zero rank.
//
// Pure function — no side effects, easy to unit test.
export function derivePositionsFromFieldTime(fieldTime: Record<string, number> | null | undefined) {
  const empty = {
    primaryPosition: '',
    secondaryPosition: '',
    positions: { P: 0, C: 0, '1B': 0, '2B': 0, '3B': 0, SS: 0, LF: 0, CF: 0, RF: 0 } as Record<string, number>,
  };
  if (!fieldTime) return empty;

  const entries = Object.entries(fieldTime)
    .filter(([pos, innings]) => innings > 0 && pos in empty.positions)
    .sort((a, b) => b[1] - a[1]);

  if (entries.length === 0) return empty;

  const positions = { ...empty.positions };
  entries.forEach(([pos, innings], i) => {
    if (i === 0) positions[pos] = 4;
    else if (i === 1) positions[pos] = 3;
    else if (innings >= 2) positions[pos] = 2;
    else positions[pos] = 1;
  });

  const primaryPosition = entries[0][0];
  const secondaryPosition = entries[1] && entries[1][1] >= 2 ? entries[1][0] : '';

  return { primaryPosition, secondaryPosition, positions };
}

